export const DAYS = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

export const DAY_NAMES_ABBR = ['Mån', 'Tis', 'Ons', 'Tors', 'Fre', 'Lör', 'Sön'];

export const plusDays = (date: Date, addition: number) => {
  if (date && addition) date.setDate(date.getDate() + addition);
  return date;
};

export const plusWeeks = (date: Date, addition: number) => {
  if (date && addition) date.setDate(date.getDate() + addition * 7);
  return date;
};

/**
 * We take a date as argument
 * and matches against list of
 * holidays
 */
export const isHoliday = (date: Date): boolean => {
  if (!date) {
    return isHoliday(new Date());
  }

  const holidays = getHolidaysForYear(date.getFullYear());

  return holidays.some(
    (holiday) =>
      holiday.getDate() === date.getDate() &&
      holiday.getMonth() === date.getMonth() &&
      holiday.getFullYear() === date.getFullYear(),
  );
};

/**
 * Accepts integers as argument then
 * validates and formats into a date instance
 */
export const fixedDate = (year: number, month: number, day: number): Date => {
  if (month > 12 || month < 1) {
    throw new Error('Incorrect month');
  }
  if (day > 31 || day < 1) {
    throw new Error('Incorrect day');
  }

  const m = month < 10 ? `0${month}` : `${month}`;
  const d = day < 10 ? `0${day}` : `${day}`;

  return new Date(`${year}-${m}-${d}`);
};

/**
 * Returns a list of swedish fixed holidays
 */
const getFixedHolidays = (year: number): Date[] => {
  return [
    fixedDate(year, 1, 1),
    fixedDate(year, 1, 6),
    fixedDate(year, 5, 1),
    fixedDate(year, 6, 6),
    fixedDate(year, 12, 24),
    fixedDate(year, 12, 25),
    fixedDate(year, 12, 26),
    fixedDate(year, 12, 31),
  ];
};

export const getExtraDaysWithoutDelivery = (year: number, brand: string): Date[] => {
  // ECOM-8992
  if (year === 2024 && brand === 'dahl') {
    return [fixedDate(year, 12, 23), fixedDate(year, 12, 27)];
  }

  return [];
};

/**
 * After we've found the easter day,
 * we can add and subtract dates to/from
 * easter and find holidays base upon rules.
 *
 * NOTE: We have not included swedish 'Halvdagar'
 *
 * Returns a list of swedish moving holidays.
 */
const getMovingHolidays = (year: number): Date[] => {
  const easterDay = getEasterDay(year);
  const holySaturday = plusDays(new Date(easterDay), -1);
  const goodFriday = plusDays(new Date(easterDay), -2);
  const easterMonday = plusDays(new Date(easterDay), 1);
  const ascensionDay = plusDays(plusWeeks(new Date(easterDay), 6), -3);
  const midsummersEve = getMidsummersEve(year);

  return [easterDay, holySaturday, goodFriday, easterMonday, ascensionDay, midsummersEve];
};

/**
 * https://sv.wikipedia.org/wiki/P%C3%A5skdagen
 *
 * We use Gauss equation to calculate easter day.
 */
const getEasterDay = (year: number): Date => {
  const goldenNumber = year % 19;
  const b = year % 4;
  const c = year % 7;
  const d = (19 * goldenNumber + 24) % 30;
  const e = (2 * b + 4 * c + 6 * d + 5) % 7;
  const easterDay = d + e > 9 ? fixedDate(year, 4, d + e - 9) : fixedDate(year, 3, 22 + d + e);
  return easterDay;
};

/**
 * https://sv.wikipedia.org/wiki/Midsommar
 *
 * We calculate midsummers eve by checking the first possible
 * friday between 19–25 June.
 */
const getMidsummersEve = (year: number): Date => {
  let firstPossibleDay = new Date(`${year}-06-19`);
  while (firstPossibleDay.getDay() !== DAYS.FRIDAY) {
    firstPossibleDay = plusDays(firstPossibleDay, 1);
  }
  return firstPossibleDay;
};

/**
 * Combines a big list of fixed and moving holdays.
 */
export const getHolidaysForYear = (year: number): Date[] => {
  if (!year) {
    return getHolidaysForYear(new Date().getFullYear());
  }

  return [getMovingHolidays(year), getFixedHolidays(year)].flat();
};

/**
 * Checks wether the date from argument is either a sunday or a saturday
 */
export const isWeekend = (date: Date): boolean => {
  if (date.getDay() === DAYS.SUNDAY || date.getDay() === DAYS.SATURDAY) {
    return true;
  }
  return false;
};

export const isToday = (date: Date): boolean => {
  const today = new Date();
  return isSameDay(date, today);
};

export const isSameDay = (dateA: Date, dateB: Date): boolean => {
  if (!(dateA instanceof Date) || !(dateB instanceof Date)) {
    throw new TypeError('dates are not instances of Date');
  }

  return dateA.toLocaleDateString() === dateB.toLocaleDateString();
};

export const isSameDayOrAfter = (dateA: Date, dateB: Date): boolean => {
  if (!(dateA instanceof Date) || !(dateB instanceof Date)) {
    throw new TypeError('dates are not instances of Date');
  }

  return isSameDay(dateA, dateB) || dateA > dateB;
};

/**
 * Determine if date is after cutoff time
 */
export const isAfterCutoffTime = ({ date = new Date(), cutoff }: { cutoff?: number | null; date?: Date }): boolean => {
  if (typeof cutoff !== 'number') return false;
  const dateInstance = date instanceof Date ? date : new Date(date);
  const currentHour = dateInstance.getHours();
  return currentHour >= cutoff;
};

/**
 * Calculate next available day that we can ship a product.
 * If the given date is before today, we use todays date instead.
 * If the next day is either a holiday or a weekend, we
 * move on to next day to do the same check until we have
 * a good looking weekday.
 */
export const nextAvailableDeliveryDate = (date?: Date | number): Date => {
  const today = new Date();

  if (!date) {
    return nextAvailableDeliveryDate(today);
  }

  let nextDate = date instanceof Date ? date : new Date(date);
  if (nextDate < today) nextDate = today;

  while (isHoliday(nextDate) || isWeekend(nextDate) || isToday(nextDate)) {
    nextDate.setDate(nextDate.getDate() + 1);
  }

  return nextDate;
};

export const formatDate = (
  dateString: string,
  locale = 'sv-SE',
  options: { dateStyle: 'medium' | 'full' | 'long' | 'short' | undefined } = { dateStyle: 'medium' },
) => {
  if (Date.parse(dateString)) {
    return new Intl.DateTimeFormat(locale, options).format(new Date(dateString));
  }
  return '';
};

export const daysAgoFromISOString = (isoString: string): string => {
  const selectedTimeStamp = new Date(isoString).getTime();
  const nowTimeStamp = new Date().getTime();
  const diffDays = (nowTimeStamp - selectedTimeStamp) / (1000 * 60 * 60 * 24);
  return `${Math.floor(diffDays)}`;
};

export const calculatePastDate = (daysAgo: number): string => {
  const today = new Date();
  const newDate = plusDays(today, daysAgo * -1);
  return newDate.toISOString();
};

export const formatToTimeZone = (dateString: string, timeZone: string): string => {
  const date = new Date(dateString);
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  const offset = utcDate.getTime() - tzDate.getTime();
  const localDate = new Date(date.getTime() + offset);

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  return new Intl.DateTimeFormat('sv-SE', { ...options, timeZone }).format(localDate);
};
